"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaClipboardList,
  FaHospital,
  FaIdCard,
  FaMapMarkerAlt,
  FaUserCircle,
} from "react-icons/fa";
import Header from "../../../components/IntranetHeader";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";
import { recordApplicationAudit } from "../../../utils/audit";

function formatRoleplayNhsNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  if (digits.length !== 10) return digits || "Not assigned";
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

function formatDateTime(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDate(value) {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function calculateAgeFromDob(value, onDate = new Date()) {
  if (!value) return null;
  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;

  let age = onDate.getFullYear() - dob.getFullYear();
  const monthDifference = onDate.getMonth() - dob.getMonth();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && onDate.getDate() < dob.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function isUpcomingAppointment(appointment) {
  if (!appointment?.appointment_datetime) return false;
  if (["Cancelled", "Completed"].includes(appointment.appointment_status)) {
    return false;
  }
  return new Date(appointment.appointment_datetime).getTime() >= Date.now();
}

function uniqueById(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    if (row?.id) map.set(row.id, row);
  });
  return Array.from(map.values());
}

export default function PatientOverview() {
  const { accountId } = useParams();
  const [account, setAccount] = useState(null);
  const [visits, setVisits] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [records, setRecords] = useState([]);
  const [careTeam, setCareTeam] = useState([]);
  const [clinicianNames, setClinicianNames] = useState({});
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOverview = useCallback(async () => {
    if (!accountId) return;

    setLoading(true);
    setError("");

    const accountResult = await supabase
      .from("patient_portal_accounts")
      .select(`
        id,
        roleplay_nhs_number,
        roleplay_name,
        roblox_user_id,
        roblox_username,
        roblox_display_name,
        date_of_birth,
        pronouns,
        height_cm,
        weight_kg,
        allergies,
        preferences,
        accessibility_needs,
        communication_preferences,
        discord_id,
        discord_username,
        profile_image_path,
        created_at,
        updated_at
      `)
      .eq("id", accountId)
      .maybeSingle();

    if (accountResult.error || !accountResult.data) {
      console.error(accountResult.error);
      setError("The patient portal account could not be loaded.");
      setLoading(false);
      return;
    }

    const portalAccount = accountResult.data;
    setAccount(portalAccount);

    const visitQueries = [
      supabase
        .from("patients")
        .select("*")
        .eq("portal_account_id", portalAccount.id)
        .order("arrival_time", { ascending: false }),
    ];

    if (portalAccount.roleplay_nhs_number) {
      visitQueries.push(
        supabase
          .from("patients")
          .select("*")
          .eq("roleplay_nhs_number", portalAccount.roleplay_nhs_number)
          .order("arrival_time", { ascending: false }),
      );
    }

    if (portalAccount.roblox_username) {
      visitQueries.push(
        supabase
          .from("patients")
          .select("*")
          .ilike("roblox_username", portalAccount.roblox_username)
          .order("arrival_time", { ascending: false }),
      );
    }

    const visitResults = await Promise.all(visitQueries);
    const visitErrors = visitResults.map((result) => result.error).filter(Boolean);

    if (visitErrors.length) {
      console.error("Unable to load hospital visits:", visitErrors);
    }

    const patientVisits = uniqueById(
      visitResults.flatMap((result) => result.data || []),
    ).sort((a, b) => {
      const aTime = new Date(a.arrival_time || a.created_at || 0).getTime();
      const bTime = new Date(b.arrival_time || b.created_at || 0).getTime();
      return bTime - aTime;
    });

    setVisits(patientVisits);

    const appointmentQueries = [
      supabase
        .from("appointments")
        .select("*")
        .eq("portal_account_id", portalAccount.id)
        .order("appointment_datetime", { ascending: false }),
    ];

    const visitIds = patientVisits.map((visit) => visit.id).filter(Boolean);
    if (visitIds.length) {
      appointmentQueries.push(
        supabase
          .from("appointments")
          .select("*")
          .in("patient_id", visitIds)
          .order("appointment_datetime", { ascending: false }),
      );
    }

    const appointmentResults = await Promise.all(appointmentQueries);
    const appointmentErrors = appointmentResults
      .map((result) => result.error)
      .filter(Boolean);

    if (appointmentErrors.length) {
      console.error("Unable to load appointments:", appointmentErrors);
    }

    const mergedAppointments = uniqueById(
      appointmentResults.flatMap((result) => result.data || []),
    ).sort(
      (a, b) =>
        new Date(b.appointment_datetime || 0).getTime() -
        new Date(a.appointment_datetime || 0).getTime(),
    );

    setAppointments(mergedAppointments);

    const clinicianIds = Array.from(
      new Set(mergedAppointments.map((appointment) => appointment.clinician).filter(Boolean)),
    );

    if (clinicianIds.length) {
      const { data: clinicianProfiles, error: clinicianError } = await supabase
        .from("profiles")
        .select("id, full_name, name_prefix")
        .in("id", clinicianIds);

      if (clinicianError) {
        console.error("Unable to load clinician names:", clinicianError);
        setClinicianNames({});
      } else {
        const names = {};
        (clinicianProfiles || []).forEach((clinician) => {
          names[clinician.id] = formatProfileName(clinician, clinician.id);
        });
        setClinicianNames(names);
      }
    } else {
      setClinicianNames({});
    }

    if (visitIds.length) {
      const [recordResult, careResult] = await Promise.all([
        supabase
          .from("patient_records")
          .select("id, patient_id, entry, added_by, created_at")
          .in("patient_id", visitIds)
          .order("created_at", { ascending: false }),
        supabase
          .from("patient_care_team")
          .select("id, patient_id, staff_name, role, added_by, created_at")
          .in("patient_id", visitIds)
          .order("created_at", { ascending: false }),
      ]);

      if (recordResult.error) {
        console.error("Unable to load clinical record activity:", recordResult.error);
        setRecords([]);
      } else {
        setRecords(recordResult.data || []);
      }

      if (careResult.error) {
        console.error("Unable to load care team history:", careResult.error);
        setCareTeam([]);
      } else {
        setCareTeam(careResult.data || []);
      }
    } else {
      setRecords([]);
      setCareTeam([]);
    }

    if (portalAccount.profile_image_path) {
      const { data: signedImage } = await supabase.storage
        .from("patient-profile-images")
        .createSignedUrl(portalAccount.profile_image_path, 60 * 60);
      setProfileImageUrl(signedImage?.signedUrl || "");
    } else if (portalAccount.roblox_username) {
      try {
        const response = await fetch("/api/robloxavatar", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username: portalAccount.roblox_username }),
        });
        const avatarResult = await response.json().catch(() => ({}));
        setProfileImageUrl(avatarResult?.imageUrl || "");
      } catch {
        setProfileImageUrl("");
      }
    } else {
      setProfileImageUrl("");
    }

    void recordApplicationAudit({
      action: "VIEW",
      tableName: "patient_portal_accounts",
      recordId: accountId,
      summary: "Patient overview viewed",
      metadata: { route: window.location.pathname },
    });

    setLoading(false);
  }, [accountId]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const activeVisits = useMemo(
    () =>
      visits.filter(
        (visit) => visit.archived !== true && Boolean(visit.arrival_time),
      ),
    [visits],
  );

  const upcomingAppointments = useMemo(
    () => appointments.filter(isUpcomingAppointment),
    [appointments],
  );

  const visitById = useMemo(() => {
    const map = new Map();
    visits.forEach((visit) => map.set(visit.id, visit));
    return map;
  }, [visits]);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="nhsuk-width-container mt-5">
          <p className="nhsuk-body">Loading patient overview…</p>
        </div>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="nhsuk-width-container mt-5">
          <div className="nhsuk-error-summary" role="alert">
            <h1 className="nhsuk-error-summary__title">Patient unavailable</h1>
            <div className="nhsuk-error-summary__body">
              <p>{error || "The patient account could not be found."}</p>
            </div>
          </div>
          <Link className="nhsuk-link" to="/intranet/ecamis/lookup">
            Back to patient lookup
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="nhsuk-width-container mt-5 pguh-staff-patient-overview" id="maincontent">
        <Link className="pguh-staff-patient-overview__back" to="/intranet/ecamis/lookup">
          <FaArrowLeft aria-hidden="true" /> Back to patient lookup
        </Link>

        <section className="pguh-staff-patient-overview__hero">
          <div className="pguh-staff-patient-overview__identity">
            <div className="pguh-staff-patient-overview__avatar">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt={`${account.roleplay_name || account.roblox_username} profile`}
                />
              ) : (
                <FaUserCircle aria-hidden="true" />
              )}
            </div>

            <div>
              <span className="pguh-eyebrow">Patient overview</span>
              <h1 className="nhsuk-heading-xl">
                {account.roleplay_name || account.roblox_display_name || account.roblox_username}
              </h1>
              <p className="nhsuk-body-l">
                @{account.roblox_username} · {formatRoleplayNhsNumber(account.roleplay_nhs_number)}
              </p>
            </div>
          </div>

          <div className="pguh-staff-patient-overview__id-card">
            <FaIdCard aria-hidden="true" />
            <span>Roleplay NHS number</span>
            <strong>{formatRoleplayNhsNumber(account.roleplay_nhs_number)}</strong>
          </div>
        </section>

        <section className="pguh-staff-patient-overview__stats">
          <div>
            <span>Current attendances</span>
            <strong>{activeVisits.length}</strong>
          </div>
          <div>
            <span>Upcoming appointments</span>
            <strong>{upcomingAppointments.length}</strong>
          </div>
          <div>
            <span>Hospital visits</span>
            <strong>{visits.length}</strong>
          </div>
          <div>
            <span>Clinical entries</span>
            <strong>{records.length}</strong>
          </div>
        </section>

        <section className="pguh-staff-patient-overview__grid">
          <article className="pguh-panel">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Patient-provided information</span>
                <h2 className="nhsuk-heading-m">Patient profile</h2>
              </div>
            </div>

            <dl className="pguh-staff-patient-overview__details">
              <div>
                <dt>Roleplay name</dt>
                <dd>{account.roleplay_name || "Not provided"}</dd>
              </div>
              <div>
                <dt>Roblox username</dt>
                <dd>@{account.roblox_username}</dd>
              </div>
              <div>
                <dt>Roblox user ID</dt>
                <dd>{account.roblox_user_id || "Not recorded"}</dd>
              </div>
              <div>
                <dt>Discord username</dt>
                <dd>{account.discord_username || "Not provided"}</dd>
              </div>
              <div>
                <dt>Discord user ID</dt>
                <dd>{account.discord_id || "Not provided"}</dd>
              </div>
              <div>
                <dt>Date of birth</dt>
                <dd>{formatDate(account.date_of_birth)}</dd>
              </div>
              <div>
                <dt>Current age</dt>
                <dd>{calculateAgeFromDob(account.date_of_birth) ?? "Not provided"}</dd>
              </div>
              <div>
                <dt>Pronouns</dt>
                <dd>{account.pronouns || "Not provided"}</dd>
              </div>
              <div>
                <dt>Height</dt>
                <dd>{account.height_cm != null ? `${account.height_cm} cm` : "Not provided"}</dd>
              </div>
              <div>
                <dt>Weight</dt>
                <dd>{account.weight_kg != null ? `${account.weight_kg} kg` : "Not provided"}</dd>
              </div>
              <div className="pguh-staff-patient-overview__details--wide">
                <dt>Allergies</dt>
                <dd>{account.allergies || "Not provided"}</dd>
              </div>
              <div className="pguh-staff-patient-overview__details--wide">
                <dt>Accessibility needs</dt>
                <dd>{account.accessibility_needs || "Not provided"}</dd>
              </div>
              <div className="pguh-staff-patient-overview__details--wide">
                <dt>Communication preferences</dt>
                <dd>{account.communication_preferences || "Not provided"}</dd>
              </div>
              <div className="pguh-staff-patient-overview__details--wide">
                <dt>Other preferences</dt>
                <dd>{account.preferences || "Not provided"}</dd>
              </div>
              <div>
                <dt>Portal account created</dt>
                <dd>{formatDate(account.created_at)}</dd>
              </div>
              <div>
                <dt>Profile last updated</dt>
                <dd>{formatDate(account.updated_at)}</dd>
              </div>
            </dl>
          </article>

          <article className="pguh-panel">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Current status</span>
                <h2 className="nhsuk-heading-m">Hospital attendance</h2>
              </div>
            </div>

            {activeVisits.length === 0 ? (
              <div className="pguh-empty-state">
                <FaHospital aria-hidden="true" />
                <h3>No current attendance</h3>
                <p>This patient is not currently shown as checked in.</p>
              </div>
            ) : (
              <div className="pguh-staff-patient-overview__active-list">
                {activeVisits.map((visit) => (
                  <div key={visit.id}>
                    <strong>{visit.location || "Hospital attendance"}</strong>
                    <span>Arrived {formatDateTime(visit.arrival_time)}</span>
                    <span>{visit.presenting_complaint || "No presenting complaint recorded"}</span>
                    <Link to={`/intranet/ecamis/patient/${visit.id}`}>Open clinical record</Link>
                  </div>
                ))}
              </div>
            )}
          </article>
        </section>

        <section className="pguh-panel pguh-staff-patient-overview__section">
          <div className="pguh-panel__header">
            <div>
              <span className="pguh-eyebrow">Appointments</span>
              <h2 className="nhsuk-heading-m">Appointment history</h2>
            </div>
          </div>

          {appointments.length === 0 ? (
            <div className="pguh-empty-state">
              <FaCalendarAlt aria-hidden="true" />
              <h3>No appointments</h3>
              <p>No appointments are currently linked to this patient account.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="nhsuk-table">
                <thead>
                  <tr>
                    <th>Date and time</th>
                    <th>Department</th>
                    <th>Clinician</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Check-in</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((appointment) => (
                    <tr key={appointment.id}>
                      <td>{formatDateTime(appointment.appointment_datetime)}</td>
                      <td>{appointment.department || "Not recorded"}</td>
                      <td>{clinicianNames[appointment.clinician] || "Not recorded"}</td>
                      <td>{appointment.location || "Not recorded"}</td>
                      <td>{appointment.appointment_status || "Scheduled"}</td>
                      <td>{appointment.checked_in_at ? formatDateTime(appointment.checked_in_at) : "Not checked in"}</td>
                      <td>
                        <Link className="nhsuk-link" to={`/intranet/ecamis/appointment/${appointment.id}`}>
                          Open appointment
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="pguh-panel pguh-staff-patient-overview__section">
          <div className="pguh-panel__header">
            <div>
              <span className="pguh-eyebrow">Attendance history</span>
              <h2 className="nhsuk-heading-m">Hospital visits</h2>
            </div>
          </div>

          {visits.length === 0 ? (
            <div className="pguh-empty-state">
              <FaHospital aria-hidden="true" />
              <h3>No hospital visits</h3>
              <p>This portal account does not yet have a linked hospital visit.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="nhsuk-table">
                <thead>
                  <tr>
                    <th>Arrival</th>
                    <th>Presenting complaint</th>
                    <th>Triage</th>
                    <th>Location</th>
                    <th>Bed</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((visit) => (
                    <tr key={visit.id}>
                      <td>{formatDateTime(visit.arrival_time)}</td>
                      <td>{visit.presenting_complaint || "Not recorded"}</td>
                      <td>{visit.triage_band || "Not recorded"}</td>
                      <td>{visit.location || "Not recorded"}</td>
                      <td>{visit.bed || "Not assigned"}</td>
                      <td>{visit.archived ? "Archived" : "Active"}</td>
                      <td>
                        <Link className="nhsuk-link" to={`/intranet/ecamis/patient/${visit.id}`}>
                          Open clinical record
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="pguh-staff-patient-overview__grid pguh-staff-patient-overview__section">
          <article className="pguh-panel">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Clinical activity</span>
                <h2 className="nhsuk-heading-m">Recent record entries</h2>
              </div>
            </div>

            {records.length === 0 ? (
              <p className="nhsuk-body">No clinical entries have been recorded.</p>
            ) : (
              <div className="pguh-staff-patient-overview__timeline">
                {records.slice(0, 10).map((record) => {
                  const visit = visitById.get(record.patient_id);
                  return (
                    <div key={record.id}>
                      <span>{formatDateTime(record.created_at)}</span>
                      <strong>{visit?.location || "Patient record"}</strong>
                      <p>{record.entry || "Clinical entry"}</p>
                      {visit && (
                        <Link to={`/intranet/ecamis/patient/${visit.id}`}>Open visit record</Link>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </article>

          <article className="pguh-panel">
            <div className="pguh-panel__header">
              <div>
                <span className="pguh-eyebrow">Care history</span>
                <h2 className="nhsuk-heading-m">Care team</h2>
              </div>
            </div>

            {careTeam.length === 0 ? (
              <p className="nhsuk-body">No care-team history is recorded.</p>
            ) : (
              <div className="pguh-staff-patient-overview__care-team">
                {careTeam.slice(0, 12).map((member) => (
                  <div key={member.id}>
                    <strong>{member.staff_name || "Staff member"}</strong>
                    <span>{member.role || "Role not recorded"}</span>
                    <small>{formatDateTime(member.created_at)}</small>
                  </div>
                ))}
              </div>
            )}
          </article>
        </section>
      </main>
    </div>
  );
}
